<template>
    <div class="ag-editable-price-input">
        <div v-if="isEditable" class="input-container">
            <vuex-number-input
                ref="numberInput"
                class="input"
                :name="`editable-price-input-${params.data.productKey}`"
                :getter="() => value"
                :setter="setValue"
                :format="format"
                :validations="validationRules"
                :make-read-only="params.context.isReadOnly()"
                hide-details
                positive-only
            />
        </div>
        <div v-else class="span-container">
            <span>{{ params.valueFormatted || formattedValue }}</span>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { isNil, get } from 'lodash';
import promoPriceCalculator from '@sharedModules/promo-price-calculator';
import rewardTypes from '@enums/reward-types';
import validators from '@/js/validators';
import promotionTabs from '@enums/promotion-tabs';
import { buildNewPriceProductUpdates } from '@/js/pages/planning/utils/product-price-update-utils';

const { roundProductPrices } = promoPriceCalculator;
const { offer } = promotionTabs;

export default defineComponent({
    data() {
        return {
            value: null,
        };
    },
    computed: {
        ...mapGetters('promotions', ['editablePriceMap']),
        format() {
            const format = get(this.params, 'format', null);
            return `numbers.default.${format}`;
        },
        isEditable() {
            if (!get(this.params, 'data.nonPromoPrices')) {
                return false;
            }
            const { namespace, tierIndex, productOfferGroupId } = this.params;
            return (
                this.editablePriceMap[namespace][tierIndex] &&
                this.editablePriceMap[namespace][tierIndex].productOfferGroups[productOfferGroupId]
            );
        },
        formattedValue() {
            return !isNil(this.value) ? this.$n(this.format, this.value) : '';
        },
        validationRules() {
            return [
                {
                    validator: validators.required,
                },
            ];
        },
        rowIndex() {
            return this.params.rowIndex;
        },
        colId() {
            return this.params.column.colId;
        },
    },
    created() {
        this.value = this.params.value;
    },
    events: {
        onPaste(event) {
            const value = parseFloat(String(event.value).replace(',', '.'));
            if (!this.isEditable || !value) {
                return false;
            }
            const eventIndex = get(event, 'node.rowIndex', null);
            const eventColId = get(event, 'column.colId', null);

            if (eventIndex === this.rowIndex && eventColId === this.colId) {
                this.setValue(value);
                // Run validation after paste, vuetify component run validation on blur
                // programaticly focus and the blur in setTimeout
                // Note: nextTick doesn't work correctly here in case when user past a lot of values
                // only last element validated with nextTick
                setTimeout(() => {
                    const textField = get(this.$refs, 'numberInput.$refs.textField', null);
                    if (textField) {
                        textField.focus();
                        textField.blur();
                    }
                }, 200);
            }
        },
    },
    methods: {
        ...mapActions('promotions', ['setProductPrice']),
        ...mapMutations('promotions', ['setUnsavedPromotion']),
        setValue(value) {
            this.value = value;
            const params = {
                value: roundProductPrices({ productPrices: { [this.params.colDef.colId]: value } })[
                    this.params.colDef.colId
                ],
                productKey: this.params.data.productKey,
                tierIndex: this.params.tierIndex || 0,
                // Column params are built once; the mechanic can change after that.
                reward: this.params.getReward
                    ? this.params.getReward(this.params.tierIndex || 0)
                    : this.params.reward,
                priceId: this.params.colDef.colId,
                namespace: this.params.namespace,
                data: this.params.data,
            };
            // need next tick for getting proper validation status
            this.$nextTick(() => {
                if (this.params.colDef.colId.indexOf('avgPrice') > -1) {
                    // For new price reward types, fix avgPrice into min and max prices
                    if (rewardTypes.newPriceRewardTypes.includes(params.reward)) {
                        const products = this.params.getPriceUpdateProducts
                            ? this.params.getPriceUpdateProducts(params)
                            : [params.data];

                        buildNewPriceProductUpdates({ products, params, value }).forEach(update =>
                            this.setProductPrice(update)
                        );
                        this.params.fixAvgPromoPrice({ ...params, products });
                    } else {
                        const { nonPromoPrices } = this.params.data;
                        const avgPriceExcTax =
                            nonPromoPrices.avgPrice === 0
                                ? 0
                                : (nonPromoPrices.avgPriceExcTax / nonPromoPrices.avgPrice) * value;
                        const avgPriceExcTaxRounded = roundProductPrices({
                            productPrices: { avgPriceExcTax },
                        }).avgPriceExcTax;
                        this.setProductPrice({
                            ...params,
                            priceId: 'avgPriceExcTax',
                            value: avgPriceExcTaxRounded,
                        });
                    }
                } else {
                    this.setProductPrice(params);
                }

                this.setUnsavedPromotion({
                    namespace: this.params.namespace,
                    tab: offer,
                    value: true,
                });
            });
        },
    },
});
</script>

<style lang="scss">
@import '@style/base/_variables.scss';
.ag-editable-price-input {
    min-width: 4rem;
    margin-left: 0.5rem;

    .input-container {
        :deep(.v-input__slot) {
            margin-top: 0.8rem;
            height: 2rem;
        }
    }

    :deep(.v-messages__message) {
        display: none;
    }
}
</style>
